import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import { requireAuth } from '~/lib/route-guards';
import type { PublicLandingPage } from '~/types';

const SCORE_DISPLAY_MS = 2000;

const scoreSearchSchema = z.object({
  score: z.number().catch(0),
  totalQuestions: z.number().catch(1),
  pointsPerCorrectAnswer: z.number().optional().catch(undefined),
});

export const Route = createFileRoute('/$slug/score')({
  validateSearch: scoreSearchSchema,
  beforeLoad: ({ context }: { context: Record<string, any> }) => {
    requireAuth(context.slug as string);
  },
  component: ScorePage,
});

function ScorePage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { score, totalQuestions, pointsPerCorrectAnswer } = Route.useSearch();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const ptsPerAnswer = pointsPerCorrectAnswer ?? landingPage.gameOptions.pointsPerCorrectAnswer;
  const maxPoints = totalQuestions * ptsPerAnswer;
  const displayScore = score * ptsPerAnswer;
  const percentage = maxPoints > 0 ? Math.round((displayScore / maxPoints) * 100) : 0;

  const handleComplete = useCallback(() => {
    router.navigate({ to: '/$slug/offer', params: { slug } });
  }, [router, slug]);

  // Auto-transition to offer after delay
  useEffect(() => {
    timerRef.current = setTimeout(handleComplete, SCORE_DISPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [handleComplete]);

  return (
    <div
      className="h-screen w-screen flex items-center justify-center p-2 sm:p-4 md:p-8 lg:p-16"
      style={{
        backgroundImage: 'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        aria-live="polite"
        className="sr-only"
      >
        Your score
      </div>
      <div className="w-full h-full md:h-auto max-w-[95vw] md:max-w-7xl md:aspect-video rounded-xl md:rounded-2xl overflow-hidden bg-white/95 shadow-2xl flex flex-col items-center justify-center p-4 md:p-12">
        <div className="flex flex-col items-center gap-4 md:gap-8 text-center animate-in fade-in zoom-in-95 duration-700">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 md:h-20 object-contain"
            />
          )}
          <div className="flex flex-col gap-2 md:gap-4">
            <h1
              className="text-4xl md:text-6xl font-bold"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {displayScore}/{maxPoints}
            </h1>
            <p className="text-sm md:text-lg font-medium text-gray-500">
              points earned out of {maxPoints} possible
            </p>
            <p
              className="text-2xl md:text-4xl font-semibold"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg md:text-2xl font-medium text-gray-700">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}
